/**
 * CompletionWeeks
 *
 * Displays workout completion data across the last 4 weeks for a single athlete.
 * Shows color-coded boxes (green for completions, white for none) with completion counts.
 *
 * @component
 * @param {Array} completionInfo - Array of week objects containing completion data
 * @param {Number} athleteId - ID of the athlete to navigate to
 * @param {Function} onWeekClick - Optional callback when week box is clicked
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useHistory } from 'react-router-dom';

const Container = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 12px;
  width: 100%;
`;

const WeekBox = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0;
  gap: 8px;
  height: 24px;
  flex: 1;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  text-align: center;
  color: #444444;
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'default')};
  transition: all 0.2s;

  ${(props) => {
    if (props.completedCount > 0) {
      return `
        background: rgba(34, 197, 94, 0.1);
        &:hover {
          background: rgba(34, 197, 94, 0.2);
        }
      `;
    }
    return `
      background: #FFFFFF;
      color: #444444;
      &:hover {
        background: ${props.isClickable ? '#f5f5f5' : '#FFFFFF'};
      }
    `;
  }}
`;

const CompletionWeeks = ({
  completionInfo, athleteId, onWeekClick, homeNavigatorEnabled,
}) => {
  const history = useHistory();
  const dataToUse = completionInfo || [];
  const numWeeks = 4;

  const sortedInfo = dataToUse.length > 0
    ? [...dataToUse]
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
      .slice(0, numWeeks)
      .reverse()
    : [];

  const weeks = Array(numWeeks)
    .fill(null)
    .map((_, index) => sortedInfo[index] || null);

  const handleWeekClick = (week) => {
    if (!week || !athleteId) {
      return;
    }
    if (onWeekClick) {
      onWeekClick(week, athleteId);
    }
    if (homeNavigatorEnabled) {
      history.push(`/dashboard/athletes/${athleteId}?targetDate=${week.startDate}`);
    } else {
      const phpAppUrl = process.env.PHP_APP_URL
        || `https://${process.env.ENVIRONMENT === 'PRODUCTION' ? '' : 'staging.'}app.teambuildr.com`;
      window.location.href = `${phpAppUrl}/manage_users?userId=${athleteId}`;
    }
  };

  return (
    <Container>
      {weeks.map((week, index) => {
        if (week) {
          return (
            <WeekBox
              key={`${week.startDate}-${week.endDate}`}
              completedCount={week.completedCount}
              isClickable
              onClick={() => handleWeekClick(week)}
            >
              {week.completedCount > 0 ? week.completedCount : '-'}
            </WeekBox>
          );
        }
        const emptyKey = `empty-${index}`;
        return (
          <WeekBox key={emptyKey} completedCount={0} isClickable={false}>
            -
          </WeekBox>
        );
      })}
    </Container>
  );
};

export default CompletionWeeks;

CompletionWeeks.propTypes = {
  completionInfo: PropTypes.arrayOf(
    PropTypes.shape({
      assignedCount: PropTypes.number,
      completedCount: PropTypes.number.isRequired,
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      completedPct: PropTypes.number.isRequired,
    }),
  ),
  athleteId: PropTypes.number,
  onWeekClick: PropTypes.func,
  homeNavigatorEnabled: PropTypes.bool,
};

CompletionWeeks.defaultProps = {
  completionInfo: [],
  athleteId: null,
  onWeekClick: null,
  homeNavigatorEnabled: false,
};
